import * as React from "react";
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    Updater,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {Checkbox} from "@/components/ui/checkbox";
import {DataTablePagination} from "@/components/data-table/data-table-pagination.tsx";
import {DataTableToolbar} from "@/components/data-table/data-table-toolbar.tsx";
import {ToolbarOptions} from "@/types/data-table/ToolbarOptions.ts";
import {useTranslation} from "react-i18next";

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    toolbarOptions: ToolbarOptions;
    hiddenColumns?: string[],
    id: string // added for generating exported filename
}

export function DataTable<TData, TValue>({
                                             columns,
                                             data,
                                             toolbarOptions,
                                             hiddenColumns,
                                             id
                                         }: DataTableProps<TData, TValue>) {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const pageParam = `${id}_page`;
    const pageSizeParam = `${id}_pageSize`;

    const [pagination, setPagination] = React.useState<PaginationState>(() => ({
        pageIndex: Math.max(0, parseInt(searchParams.get(pageParam) ?? "1", 10) - 1),
        pageSize: parseInt(searchParams.get(pageSizeParam) ?? "10", 10),
    }));

    const handlePaginationChange = React.useCallback(
        (updater: Updater<PaginationState>) => {
            setPagination((prev) => {
                const next = typeof updater === "function" ? updater(prev) : updater;
                setSearchParams(
                    (p) => {
                        const params = new URLSearchParams(p);
                        if (next.pageIndex === 0) params.delete(pageParam);
                        else params.set(pageParam, String(next.pageIndex + 1)); // 1-based in URL
                        if (next.pageSize === 10) params.delete(pageSizeParam);
                        else params.set(pageSizeParam, String(next.pageSize));
                        return params;
                    },
                    { replace: true }
                );
                return next;
            });
        },
        [setSearchParams, pageParam, pageSizeParam]
    );

    const [rowSelection, setRowSelection] = React.useState({});

    const initialState: VisibilityState = hiddenColumns
        ? hiddenColumns.reduce((acc: VisibilityState, column: string) => {
            acc[column] = false;
            return acc;
        }, {})
        : {};
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>(initialState);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        [],
    );
    const [sorting, setSorting] = React.useState<SortingState>([]);

    const selectionColumn: ColumnDef<TData, TValue> = React.useMemo(
        () => ({
            id: "select",
            header: ({ table }) => {
                const rows = table.getFilteredRowModel().rows;
                const allSelected =
                    rows.length > 0 && rows.every((r) => r.getIsSelected());
                const someSelected = rows.some((r) => r.getIsSelected());
                return (
                    <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={(value) => {
                            const checked = value === true;
                            if (checked) {
                                const sel: Record<string, boolean> = {};
                                rows.forEach((r) => {
                                    sel[r.id] = true;
                                });
                                setRowSelection(sel);
                            } else {
                                setRowSelection({});
                            }
                        }}
                        aria-label={t("dataTable.toolbar.selectAllAcrossPages")}
                        title={t("dataTable.toolbar.selectAllAcrossPages")}
                        className="translate-y-0.5"
                    />
                );
            },
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label={t("dataTable.toolbar.toggleRow")}
                    className="translate-y-0.5"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        }),
        [setRowSelection, t]
    );

    const columnsWithSelection = React.useMemo(
        () => [selectionColumn, ...columns],
        [selectionColumn, columns]
    );

    const table = useReactTable({
        data,
        columns: columnsWithSelection,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onPaginationChange: handlePaginationChange,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    return (
        <div className="space-y-4">
            <DataTableToolbar table={table} id={id} toolbarOptions={toolbarOptions}/>
            <div className="rounded-xl border border-border bg-bg-card overflow-hidden shadow-sm">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columnsWithSelection.length}
                                    className="h-24 text-center"
                                >
                                    {t("dataTable.noResults")}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <DataTablePagination table={table}/>
        </div>
    );
}
